import React from 'react';
/**
 * @component Playhead
 *
 * @description A reusable playhead component for video playback.
 * Displays a draggable handle that shows the current playback position and allows seeking.
 *
 * @example
 * // Basic usage
 * <Playhead
 *   ref={playheadRef}
 *   duration={videoDuration}
 *   currentTime={videoCurrentTime}
 *   onSeek={handleSeek}
 *   onDragStart={handleDragStart}
 *   onDragEnd={handleDragEnd}
 *   onDrag={handleDrag}
 * />
 */

import { useState, forwardRef, useEffect } from 'react';
import PropTypes from 'prop-types';

const Playhead = forwardRef(
  (
    {
      // Required props
      duration = 0,
      currentTime = 0,
      onSeek,
      onDragStart,
      onDragEnd,
      onDrag,

      // Optional props with defaults
      isLive = false,
      dragging = false,
      scrubTime = 0,
      displayMarkers = [],
      handleColor = '#fff',
      handleBorderColor = '#333',
      handleSize = 16,
      handleLineColor = '#000',
      ...otherProps
    },
    ref
  ) => {
    const [refBounds, setRefBounds] = useState(null);

    /**
     * Handle mouse down event on the playhead handle
     * Sets dragging state and stores current bounds
     */
    const handleMouseDown = (e) => {
      if (!ref?.current || !onDragStart) return;

      setRefBounds(ref.current.getBoundingClientRect());
      onDragStart(currentTime);
    };

    /**
     * Handle mouse up event
     * Ends dragging and seeks to the current position
     */
    const handleMouseUp = () => {
      if (!onDragEnd) return;
      // only fire if dragging.
      if (!dragging) return;
      onDragEnd(dragging ? scrubTime : currentTime);
    };

    /**
     * Handle mouse move event
     * Updates the scrub time based on mouse position when dragging
     */
    const handleMouseMove = (e) => {
      if (!dragging || !refBounds || !onDrag) return;

      let relativeX = e.clientX - refBounds.left;
      relativeX = Math.max(0, Math.min(relativeX, refBounds.width));
      let newTime = (relativeX / refBounds.width) * duration;
      newTime = Math.round(newTime);

      onDrag(newTime);
    };

    /**
     * Handle click event on the playhead area
     * Directly seeks to the clicked position
     */
    const handleClickNav = (e) => {
      if (!ref?.current || !onSeek) return;

      const rect = ref.current.getBoundingClientRect();
      let relativeX = e.clientX - rect.left;
      relativeX = Math.max(0, Math.min(relativeX, rect.width));
      const newTime = (relativeX / rect.width) * duration;

      onSeek(newTime);
    };

    /**
     * Calculate the position percentage for the playhead
     */
    const getPositionPercentage = () => {
      if (duration <= 0) return 0;
      const time = dragging ? scrubTime : currentTime;
      return (time / duration) * 100;
    };

    /**
     * Style for the playhead handle
     */
    const handleStyle = {
      position: 'absolute',
      top: '-5px', // adjust vertical position as needed
      left: `${getPositionPercentage()}%`,
      width: `${handleSize}px`,
      height: `${handleSize}px`,
      borderRadius: '50%',
      background: handleColor,
      border: `1px solid ${handleBorderColor}`,
      cursor: 'pointer',
      zIndex: 9100,
      transform: 'translateX(-50%)',
      display: isLive ? 'none' : 'block',
      ...otherProps.handleStyle,
    };

    /**
     * Style for the playhead line
     */
    const handleLineStyle = {
      position: 'absolute',
      top: '11px', // adjust vertical position as needed
      left: `${getPositionPercentage()}%`,
      width: displayMarkers.length ? '1px' : '0px',
      height: `${displayMarkers.length * 20 + 25}px`,
      background: handleLineColor,
      cursor: 'pointer',
      zIndex: 9000,
      display: isLive ? 'none' : 'block',
      ...otherProps.handleLineStyle,
    };

    return (
      <>
        <div
          style={handleStyle}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onClick={handleClickNav}
          id="playback-handle"
          role="slider"
          aria-label="Video playhead"
          aria-valuemin="0"
          aria-valuemax={duration}
          aria-valuenow={dragging ? scrubTime : currentTime}
          tabIndex="0"
          data-testid="playhead-handle"
        />
        <div
          style={handleLineStyle}
          id="playback-handle-line"
          data-testid="playhead-line"
        />
      </>
    );
  }
);

/**
 * PropTypes for the Playhead component
 */
Playhead.propTypes = {
  /**
   * The total duration of the media in seconds
   */
  duration: PropTypes.number,

  /**
   * The current playback position in seconds
   */
  currentTime: PropTypes.number,

  /**
   * Callback function called when the user seeks to a new position
   * @param {number} time - The new time position in seconds
   */
  onSeek: PropTypes.func.isRequired,

  /**
   * Callback function called when the user starts dragging the playhead
   * @param {number} time - The time position when dragging started
   */
  onDragStart: PropTypes.func.isRequired,

  /**
   * Callback function called when the user ends dragging the playhead
   * @param {number} time - The final time position after dragging
   */
  onDragEnd: PropTypes.func.isRequired,

  /**
   * Callback function called during dragging of the playhead
   * @param {number} time - The current time position during dragging
   */
  onDrag: PropTypes.func.isRequired,

  /**
   * Whether the media is being played live
   */
  isLive: PropTypes.bool,

  /**
   * Whether the user is currently dragging the playhead
   */
  dragging: PropTypes.bool,

  /**
   * The time position when scrubbing/dragging in seconds
   */
  scrubTime: PropTypes.number,

  /**
   * Array of markers to display on the timeline
   */
  displayMarkers: PropTypes.array,

  /**
   * Color of the playhead handle
   */
  handleColor: PropTypes.string,

  /**
   * Border color of the playhead handle
   */
  handleBorderColor: PropTypes.string,

  /**
   * Size of the playhead handle in pixels
   */
  handleSize: PropTypes.number,

  /**
   * Color of the playhead line
   */
  handleLineColor: PropTypes.string,

  /**
   * Additional styles for the handle element
   */
  handleStyle: PropTypes.object,

  /**
   * Additional styles for the handle line element
   */
  handleLineStyle: PropTypes.object,
};

export default Playhead;
